import Router from 'next/router';
import { lastValueFrom } from 'rxjs';
import { isAnyOf } from '@reduxjs/toolkit';

import {
  changePassword, changePasswordError, changePasswordSuccess,
  connectSocialNetworkUser,
  connectUser, connectUserError, connectUserSuccess,
  connectedUser, connectedUserError, connectedUserSuccess,
  createUser, createUserError, createUserSuccess,
  disconnectUser, disconnectUserSuccess,
  resetPassword, resetPasswordError, resetPasswordSuccess
} from '../slices/auth.slice';

import { hideLoginModal, hidePageProgress, showPageProgress } from '../slices/gui.slice';
import { primeurClearUser } from '../slices/primeur.slice';
import { getUser } from '../slices/user.slice';
import { getCart, resetCart } from '../slices/cart.slice';
import { getBox, resetBox } from '../slices/box.slice';
import { setActAsCustomerSuccess } from '../slices/admin.slice';
import { AuthUser, SocialNetworkInformation } from '@/models';
import { AppDispatch, AppListenerEntry } from '@/redux';
import { LocalStorageService } from '@/services/LocalStorage.service';
import { STORE_ID_DEFAULT } from '@/constants/stores';
import { KEYS } from '@/constants/keys';

export const epicCreateUser: AppListenerEntry = {
  actionCreator: createUser,
  effect: async ({ payload }: ReturnType<typeof createUser>, { getState, dispatch, extra: { authService } }) => {
    const { root } = getState();
    const { user, redirectUrl } = payload;
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    try {
      dispatch(showPageProgress());
      const createdUser = await lastValueFrom(authService.create(storeId, user)) as AuthUser;
      processAuthSuccess(dispatch, true, createdUser, redirectUrl);
    } catch (error) {
      dispatch(createUserError({ error }));
    }
  }
};

export const epicConnectUser: AppListenerEntry = {
  actionCreator: connectUser,
  effect: async ({ payload }: ReturnType<typeof connectUser>, { getState, dispatch, extra: { authService } }) => {
    const { root } = getState();
    const { email, password, redirectUrl } = payload;
    const { storeId = STORE_ID_DEFAULT, privateWebsite = false } = root.storeConf;
    try {
      dispatch(showPageProgress());
      const user = await lastValueFrom(authService.connect(storeId, email, password, privateWebsite)) as AuthUser;
      processAuthSuccess(dispatch, false, user, redirectUrl);
    } catch (error) {
      dispatch(connectUserError({ error }));
    }
  }
};

export const epicConnectSocialNetworkUser: AppListenerEntry = {
  actionCreator: connectSocialNetworkUser,
  effect: async ({ payload }: ReturnType<typeof connectSocialNetworkUser>, { getState, dispatch, extra: { authService } }) => {
    const { root } = getState();
    const { socialNetworkInformation } = payload as { socialNetworkInformation: SocialNetworkInformation };
    const { storeId = STORE_ID_DEFAULT } = root.storeConf;
    try {
      dispatch(showPageProgress());
      const connectedUser = await lastValueFrom(authService.connectSocialNetwork(storeId, socialNetworkInformation)) as AuthUser;
      processAuthSuccess(dispatch, false, connectedUser, socialNetworkInformation.redirectUrl);
    } catch (error) {
      dispatch(connectUserError({ error }));
    }
  }
};

export const epicUserAuthSuccess: AppListenerEntry = {
  matcher: isAnyOf(
    createUserSuccess,
    connectUserSuccess
  ),
  effect: (_action: ReturnType<typeof createUserSuccess>, { dispatch }) => {
    LocalStorageService.remove(KEYS.HASH_USER);

    [hideLoginModal(), hidePageProgress(), primeurClearUser(), getUser(), getCart(), getBox()].forEach((a) => dispatch(a));
  }
};

export const epicDisconnectUser: AppListenerEntry = {
  actionCreator: disconnectUser,
  effect: (_action: ReturnType<typeof disconnectUser>, { getState, dispatch, extra: { authService } }) => {
    const { root: { storeConf: { privateWebsite } } } = getState();
    authService.disconnect(privateWebsite);
    dispatch(disconnectUserSuccess());
  }
};

export const epicResetCartOnDisconnection: AppListenerEntry = {
  matcher: isAnyOf(
    createUserError,
    connectUserError,
    disconnectUserSuccess,
    setActAsCustomerSuccess
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    [hidePageProgress(), resetBox()].forEach((a) => dispatch(a));
  }
};

export const epicGetConnectedUser: AppListenerEntry = {
  actionCreator: connectedUser,
  effect: (_action: ReturnType<typeof connectedUser>, { dispatch, extra: { authService } }) => {
    try {
      const user = authService.getConnectedUser();
      dispatch(user ? connectedUserSuccess({ user }) : connectedUserError());
    } catch (error) {
      connectedUserError();
    }
  }
};

export const epicResetPassword: AppListenerEntry = {
  actionCreator: resetPassword,
  effect: async ({ payload }: ReturnType<typeof resetPassword>, { getState, dispatch, extra: { authService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { email } = payload;
    try {
      await lastValueFrom(authService.resetPassword(storeId, email));
      dispatch(resetPasswordSuccess());
    } catch (error) {
      dispatch(resetPasswordError({ error }));
    }
  }
};

export const epicChangePassword: AppListenerEntry = {
  actionCreator: changePassword,
  effect: async ({ payload }: ReturnType<typeof changePassword>, { getState, dispatch, extra: { authService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { password, token } = payload;
    try {
      await lastValueFrom(authService.changePassword(storeId, password, token));
      dispatch(changePasswordSuccess());
    } catch (error) {
      dispatch(changePasswordError({ error }));
    }
  }
};

const processAuthSuccess = (dispatch: AppDispatch, isCreate: boolean, user: AuthUser, redirectUrl?: string) => {
  dispatch(isCreate ? createUserSuccess({ user }) : connectUserSuccess({ user, redirectUrl }));
  if (redirectUrl) {
    Router.push(redirectUrl);
  }
};
